// AskButlerChip — chat handoff from a help topic/step.
//
// Seeds ChatContext's pendingMessage with the topic's pre-written chat
// prompt and navigates to /home, where ChatInterface is promoted inline
// and auto-sends pendingMessage on the next connected turn (existing
// pattern in ChatContext.tsx:822 — pendingMessage triggers sendMessage).

import { useNavigate } from 'react-router-dom'
import { MessageCircle } from 'lucide-react'

import { useChat } from '@/contexts/ChatContext'
import { useOperator } from '@/contexts/OperatorContext'

interface AskButlerChipProps {
  seed: string
  label?: string
  /**
   * Render as a large footer CTA (topic-level handoff) vs an inline
   * chip next to a step. Both use the same handler.
   */
  variant?: 'footer' | 'inline'
  /**
   * Optional callback for parent to observe the handoff — used by
   * tests that assert the seed payload reached the context.
   */
  onHandoff?: (seed: string) => void
}

export function AskButlerChip({
  seed,
  label,
  variant = 'inline',
  onHandoff,
}: AskButlerChipProps) {
  const { setPendingMessage } = useChat()
  const { short: brandName } = useOperator()
  const resolvedLabel = label ?? `Ask ${brandName} to do this for me`
  const navigate = useNavigate()

  const handleClick = () => {
    const trimmed = seed.trim()
    if (!trimmed) return
    setPendingMessage(trimmed)
    onHandoff?.(trimmed)
    navigate('/home')
  }

  const classes =
    variant === 'footer'
      ? 'inline-flex items-center gap-2 rounded-lg bg-butler-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-butler-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-butler-500/60'
      : 'inline-flex items-center gap-1.5 rounded-full border border-butler-500/40 bg-white px-3 py-1 text-xs font-medium text-butler-600 transition hover:border-butler-500 hover:bg-butler-500/10 dark:bg-slate-900 dark:text-butler-200'

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="help-ask-butler"
      data-variant={variant}
      className={classes}
    >
      <MessageCircle aria-hidden="true" className="h-4 w-4" />
      {resolvedLabel}
    </button>
  )
}
